import _get from 'lodash/get';
import _set from 'lodash/set';
import _merge from 'lodash/merge';
import _reduce from 'lodash/reduce';
import _cloneDeep from 'lodash/cloneDeep';

class DfpRequestBuilder {

    // Documentation: https://support.google.com/dfp_premium/answer/1068325?hl=en

    /**
     *
     * @access public
     * @param {Object} config
     * @param {String} [config.url='https://pubads.g.doubleclick.net/gampad/live/ads']
     * @param {String} [config.sz='400x300|640x480|1920x1280']
     * @param {String} [config.iu='/2605/video_test/riot']
     * @param {String} [config.impl='s']
     * @param {Number} [config.gdfp_req=1]
     * @param {String} [config.env='vp']
     * @param {String} [config.output='vast']
     * @param {Number} [config.unviewed_position_start=1]
     * @param {String} [config.description_url='[description_url]']
     * @param {String} [config.correlator='[timestamp]']
     * @param {Number} [config.pmnd=0]
     * @param {Number} [config.pmxd=60000]
     * @param {Number} [config.pmad=-1]
     * @param {String} [config.vpos='preroll']
     * @param {Number} [config.pod=1]
     * @param {Number} [config.ad_rule=0]
     * @param {String} [config.asset='']
     * @param {String} [config.dfp.additionalParams={}] Optional. Merged if additional param properties exist. Additoinal property specific params.
     * @param {Object} clip
     * @param {Object} [clip.adConfig={}]
     * @param {String} [clip.adConfig.cust_params='']
     * @param {String} [clip.adConfig.vid=''] matchPk, contentId or gamePk based on media type
     * @returns {String} url query param
     * @desc picks off values from config and clip and returns a url query string.<br>
     *     <a href="https://support.google.com/dfp_premium/answer/1068325?hl=en">https://support.google.com/dfp_premium/answer/1068325?hl=en</a>
     * @memberof DFPRequestBuilder
     */
    static build(config, clip, cuePointVO) {

        // defaults here are geared towards preroll specification
        const adConfig = _cloneDeep(_get(clip, 'adConfig', {}));

        // attempt to get 'url' from the adConfig, then from 'config', defaulting to hardcoded value
        const url = _get(adConfig, 'url', _get(config, 'url', 'https://pubads.g.doubleclick.net/gampad/live/ads'));

        let mapper = {
            sz: _get(config, 'sz', '400x300|640x480|1920x1280'),
            iu: _get(config, 'iu'),
            impl: _get(config, 'impl', 's'),
            gdfp_req: _get(config, 'gdfp_req', 1),
            env: _get(config, 'env', 'vp'),
            output: _get(config, 'output', 'vast'),
            unviewed_position_start: _get(config, 'unviewed_position_start', '1'),
            url: _get(config, 'url', '[referrer_url]'),
            description_url: _get(config, 'description_url', '[description_url]'),
            correlator: _get(config, 'correlator', '[timestamp]'),
            pmnd: _get(config, 'pmnd', '0'),
            pmxd: _get(config, 'pmxd', '60000'),
            pmad: _get(config, 'pmad', '1'),
            vpos: _get(config, 'vpos', 'preroll'),
            pp: _get(config, 'pp', 'web'),
            pod: _get(config, 'pod', '1'),
            ad_rule: _get(config, 'ad_rule', '1'),
            asset: _get(config, 'asset', ''),
        };

        if (config.getDfpParams) {

            mapper = config.getDfpParams(config);

        }

        const additionalParams = _get(config, 'additionalParams', {});
        const event = _get(cuePointVO, 'event');

        // Add the `event` cuepoint data to the custom_params
        if (event !== undefined) {

            _set(adConfig, 'cust_params', _merge({}, _get(adConfig, 'cust_params', {}), { evnt: event }));

        }

        // Moving the logic to get the dynamic custom params and add the new one to a new method
        const custParams = DfpRequestBuilder.addCustomParams(adConfig, config);

        if (custParams !== undefined) {

            adConfig.cust_params =  DfpRequestBuilder.encodeCustomParams(custParams);

        }


        // Merge adConfig params & additionalParams
        _merge(mapper, adConfig, additionalParams);

        const query = _reduce(mapper, function(result, value, key) {

            return result + (result ? '&' : '') + key + '=' + value;

        }, '');

        const rtn = url+ '?' + query;

        return rtn;

    }

    static encodeCustomParams(params) {

        var encodedParams = "";

        for (var key in params) {

            if (params.hasOwnProperty(key)) {

                encodedParams = encodedParams + key + "%3D" + params[key] + "%26";

            }

        }

        return encodedParams.substring(0, encodedParams.length - 3);

    }

    /**
     *
     * @desc get dynamic cust_params from the adConfig, and add other values from the config
     *
     */

    static addCustomParams(adConfig, config) {

        const adConfigCustParams = _get(adConfig, 'cust_params', {});
        const configCustParams = _get(config, 'cust_params', {});
        const params = _merge({}, configCustParams, adConfigCustParams);

        return {

            ...params,
            topic: _get(config, 'topicId'),
            width: _get(config, 'width'),
            height: _get(config, 'height'),

        };

    }

}

export default DfpRequestBuilder;
